import classNames from 'classnames';
import { fromJS } from 'immutable';
import moment from 'moment-timezone';
import React from 'react';
import { Row, Col } from 'react-bootstrap';
import { WindowScroller, List, InfiniteLoader } from 'react-virtualized';
import TimeUtil from 'time';
import smoothscroll from 'smoothscroll-polyfill';

import TimeDisplayTypes from 'shared/constants/TimeDisplayTypes';
import HighlightCellHelper from 'main/components/show/cuesheet/highlightCellHelper';
import {
  ColumnProps,
  ListHeaderHeight,
  PageHeaderHeight,
  RowCount,
} from 'shared/flux/stores/cuesheet.fullscreen/DisplayViewConstants';
import FullScreenCuesheetActions from 'flux/actions/cuesheet.fullscreen/FullScreenCuesheetActions';

// NOTE THIS FILE HAS MANY PROP TYPE VIOLATIONS
// AND ALL ARE DISABLED BY THE COMMENT BELOW.
/* eslint-disable react/prop-types */

//we add this polyfill so that safari and firefox will have an animated
//smooth scroll to a tracked row instead of jumping straight to it
smoothscroll.polyfill();

// const STATUS_LOADING = 1;
// const STATUS_LOADED = 2;

class RowList extends React.Component {
  constructor(props) {
    super(props);
    this._updateDimensions = this._updateDimensions.bind(this);
    this._isRowLoaded = this._isRowLoaded.bind(this);
    this._loadMoreRows = this._loadMoreRows.bind(this);
    this._getFormattedRow = this._getFormattedRow.bind(this);
    this._rowRenderer = this._rowRenderer.bind(this);
    this._getRowIdsByIndexRange = this._getRowIdsByIndexRange.bind(this);
    this._setInfiniteLoaderRef = this._setInfiniteLoaderRef.bind(this);

    const { listHeight, listWidth } = _getListDimensions();

    this._timeoutIdMap = {};

    this.state = {
      listHeight,
      listWidth,
      rowHeight: listHeight / RowCount,
    };

    /**
     * Add event listener
     */
    window.addEventListener('resize', this._updateDimensions);
  }

  /**
   * Remove event listener
   */
  componentWillUnmount() {
    this._resetLastScrollIndex();

    window.removeEventListener('resize', this._updateDimensions);
    Object.keys(this._timeoutIdMap).forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.rowMap !== this.props.rowMap) {
      if (this.promise) {
        this._resolvePromise();
      }
      if (this._infiniteLoaderRef && this._infiniteLoaderRef._registeredChild) {
        this._infiniteLoaderRef._registeredChild.forceUpdateGrid();
      }
    }

    const { scrollToIndex, lastScrollToIndex } = this.props;

    const currentPosition = window.scrollY;
    const { rowHeight } = this.state;
    const scrollToPosition = rowHeight * scrollToIndex;

    //when refreshing a page, chrome ( and possibly other browsers ) reloads the page at the same
    //scroll position. if this happens we need to update the scrollToIndex to reflect that we've
    //already scrolled to the correct position
    if (
      currentPosition === scrollToPosition &&
      scrollToIndex > 0 &&
      scrollToIndex !== lastScrollToIndex
    ) {
      FullScreenCuesheetActions.updateScrollIndex(scrollToIndex);
    }

    if (
      currentPosition !== scrollToPosition &&
      scrollToIndex !== lastScrollToIndex
    ) {
      window.scroll({
        top: scrollToPosition,
        left: 0,
        behavior: 'smooth',
      });
      FullScreenCuesheetActions.updateScrollIndex(scrollToIndex);
    }
  }

  render() {
    const { rowOrder } = this.props;
    const { listHeight, listWidth, rowHeight } = this.state;

    const rowCount = rowOrder.length || RowCount;

    const listPadding = PageHeaderHeight + ListHeaderHeight;
    const gradientOffset = PageHeaderHeight + ListHeaderHeight;

    return (
      <InfiniteLoader
        isRowLoaded={this._isRowLoaded}
        loadMoreRows={this._loadMoreRows}
        rowCount={rowCount}
        ref={this._setInfiniteLoaderRef}
      >
        {({ onRowsRendered, registerChild }) => (
          <div
            className="window-scroller-wrapper"
            style={{ height: listHeight, paddingTop: listPadding }}
          >
            <WindowScroller>
              {({ height, isScrolling, onChildScroll, scrollTop }) => (
                <div id="display-view-list-wrapper">
                  <div
                    id="onboarding-row-list-overlay"
                    style={{ height: `${rowHeight * rowCount}` }}
                  />
                  <div
                    id="gradient-overlay-top"
                    style={{ top: gradientOffset, height: rowHeight }}
                  />
                  <div
                    id="gradient-overlay-bottom"
                    style={{ height: rowHeight }}
                  />
                  <List
                    autoHeight
                    ref={registerChild}
                    height={height}
                    onRowsRendered={onRowsRendered}
                    isScrolling={isScrolling}
                    onScroll={onChildScroll}
                    scrollTop={scrollTop}
                    rowCount={rowCount}
                    rowHeight={rowHeight}
                    rowRenderer={this._rowRenderer}
                    width={listWidth}
                  />
                </div>
              )}
            </WindowScroller>
          </div>
        )}
      </InfiniteLoader>
    );
  }

  _resetLastScrollIndex() {
    FullScreenCuesheetActions.resetLastScrollIndex();
  }

  _setInfiniteLoaderRef(ref) {
    this._infiniteLoaderRef = ref;
  }

  _updateDimensions() {
    const { listHeight, listWidth } = _getListDimensions();

    this.setState({
      listHeight,
      listWidth,
      rowHeight: listHeight / RowCount,
    });
  }

  _getRowIdsByIndexRange(startIndex, stopIndex) {
    const { rowOrder } = this.props;

    return rowOrder.slice(startIndex, stopIndex + 1);
  }

  _rowRenderer({ key, index, style }) {
    const row = this._getFormattedRow(index);

    if (index % 2 === 1) {
      style.background = '#333649';
    } else {
      style.background = '#1E2130';
    }

    return (
      <div key={key} style={style}>
        {row}
      </div>
    );
  }

  _isRowLoaded({ index }) {
    const { itemSetId, rowMap, rowOrder } = this.props;

    const rowId = rowOrder[index];

    return rowMap && rowMap[itemSetId].rows[rowId];
  }

  _loadMoreRows({ startIndex, stopIndex }) {
    const { columnId, eventId, itemSetId, showId, teamId, rowMap } = this.props;

    this.promise = new Promise((res) => {
      this._resolvePromise = res;
    });

    const rowIdsToLoad = [];

    this._getRowIdsByIndexRange(startIndex, stopIndex).forEach((rowId) => {
      if (!rowMap || !rowMap[itemSetId].rows[rowId]) rowIdsToLoad.push(rowId);
    });

    if (rowIdsToLoad.length === 0) return this.promise;

    FullScreenCuesheetActions.populateItemSetSubsetBuffered(
      teamId,
      eventId,
      showId,
      [columnId],
      rowIdsToLoad,
    );

    return this.promise;
  }

  _getFormattedRow(index) {
    const {
      columnId,
      event,
      itemSetId,
      rowMap,
      rowOrder,
      show,
      scrollToIndex,
      trackingEnabled,
      loadingColumnData,
    } = this.props;

    const rowId = rowOrder[index];

    if (!rowMap || !rowMap[itemSetId].rows[rowId]) return <PlaceholderRow />;

    const rowIndex = rowOrder.indexOf(rowId);

    const { item, tracked } = rowMap[itemSetId].rows[rowId];

    const {
      cells,
      displayDurationClock,
      displayPresetClock,
      displayStartTimeClock,
      durationInSeconds,
      numberAdjusted,
      presetDurationInSecondsAdjusted,
    } = item;

    const {
      displayDuration,
      displayPresetDuration,
      displayStartTime,
      durationDisplayType,
      presetDurationDisplayType,
    } = show;

    const { twentyFourHourClock } = event;

    const title = item.title || '';

    const showStartOn = show.startOn;

    const itemStartOn = item.startOnAdjusted;

    let startTimeValue = itemStartOn || moment().startOf('day').valueOf();

    if (rowIndex === 0 && !itemStartOn) {
      startTimeValue = showStartOn;
    }

    const startTimeText =
      displayStartTimeClock && displayStartTime
        ? TimeUtil.getShorthandTimeUTC(startTimeValue, twentyFourHourClock)
        : ' ';
    const startTimeNode = <div className="start-time">{startTimeText}</div>;

    const presetValue = presetDurationInSecondsAdjusted;
    let presetText = TimeUtil.getShorthandFormattedPresetDuration(presetValue);

    if (presetDurationDisplayType === TimeDisplayTypes.sixDigitTime) {
      presetText = TimeUtil.getFormattedDuration(presetValue);
    }

    if (presetDurationDisplayType === TimeDisplayTypes.presetTime) {
      presetText = TimeUtil.getFormattedPresetDuration(presetValue);
    }

    if (!displayPresetClock || !displayPresetDuration) presetText = ' ';

    let presetTimeClasses = classNames('preset-time', {
      over: presetDurationInSecondsAdjusted < 0,
    });

    const presetTimeNode = (
      <div className={presetTimeClasses}>{presetText}</div>
    );

    const durationValue = durationInSeconds;
    let durationText = TimeUtil.getShorthandDuration(durationValue);

    if (durationDisplayType === TimeDisplayTypes.clockTime) {
      durationText = TimeUtil.getShorthandFormattedPresetDuration(
        durationInSeconds,
      );
    }
    if (durationDisplayType === TimeDisplayTypes.sixDigitTime) {
      durationText = TimeUtil.getFormattedDuration(durationInSeconds);
    }

    if (!displayDurationClock || !displayDuration) durationText = ' ';

    const durationNode = <div className="duration">{durationText}</div>;

    const hasContent = cells[columnId] ? cells[columnId].text : false;

    let columnText = hasContent || '';

    if (loadingColumnData) {
      columnText = '...';
    }

    const globalHightlightStyles = HighlightCellHelper.getGlobalHighlightStyle(
      fromJS(item),
    );

    const {
      numberColumnProps,
      titleColumnProps,
      timingColumnProps,
      dataColumnProps,
    } = ColumnProps;

    const itemNumberContextNode =
      trackingEnabled && scrollToIndex >= 0 ? (
        <div className="item-number-context">
          {rowIndex === scrollToIndex + 1 ? 'NOW' : null}
          {rowIndex === scrollToIndex + 2 ? 'NEXT' : null}
        </div>
      ) : null;

    return (
      <div key={rowId} className="display-view-row-wrapper">
        <Row
          className={classNames('display-view-row', { tracked: tracked })}
          style={globalHightlightStyles}
        >
          <Col {...numberColumnProps} className="item-number-cell">
            <div className="item-number">{numberAdjusted}</div>
            {itemNumberContextNode}
          </Col>
          <Col {...titleColumnProps} className="item-title-cell">
            <div className="item-title">{title}</div>
          </Col>
          <Col {...timingColumnProps} className="timings-cell">
            {startTimeNode}
            {presetTimeNode}
            {durationNode}
          </Col>
          <Col {...dataColumnProps} className="data-cell">
            <div className="data-text">{columnText}</div>
          </Col>
        </Row>
      </div>
    );
  }
}

function _getListDimensions() {
  return {
    listHeight: window.innerHeight - PageHeaderHeight - ListHeaderHeight,
    listWidth: window.innerWidth,
  };
}

const PlaceholderRow = () => {
  const {
    numberColumnProps,
    titleColumnProps,
    timingColumnProps,
    dataColumnProps,
  } = ColumnProps;

  return (
    <div className="display-view-row-wrapper">
      <Row className="display-view-row">
        <Col {...numberColumnProps} className="item-number-cell">
          <div className="item-number">...</div>
          <div className="item-number-context" />
        </Col>
        <Col {...titleColumnProps} className="item-title-cell">
          <div className="item-title">...</div>
        </Col>
        <Col {...timingColumnProps} className="timings-cell">
          <div className="start-time">8:00am</div>
          <div className="preset-time">0m</div>
          <div className="duration">0s</div>
        </Col>
        <Col {...dataColumnProps} className="data-cell">
          <div className="data-text">...</div>
        </Col>
      </Row>
    </div>
  );
};

export default RowList;
